import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormControl, FormDescription, FormField, FormItem, FormLabel } from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CampaignForm } from '@/modules/campaigns/utils';
import { senderFieldPath } from '@/modules/campaigns/utils/senderFieldPath';
import { AmdOnMachine } from '@/shared/types/campaigns';

// Sentinel for the platform default (keep the call going): Radix SelectItem
// forbids empty values; read-back normalizes a stored 'continue' to null.
const CONTINUE = 'continue';

type AmdActionOption = AmdOnMachine | typeof CONTINUE;

const AMD_ACTION_OPTIONS: AmdActionOption[] = [CONTINUE, 'hangup', 'opener_then_hangup'];

const AMD_ACTION_LABEL_KEYS: Record<AmdActionOption, string> = {
  continue: 'campaigns.build.amd_action_continue',
  hangup: 'campaigns.build.amd_action_hangup',
  opener_then_hangup: 'campaigns.build.amd_action_opener_then_hangup',
};

const AMD_ACTION_HINT_KEYS: Record<AmdActionOption, string> = {
  continue: 'campaigns.build.amd_action_continue_hint',
  hangup: 'campaigns.build.amd_action_hangup_hint',
  opener_then_hangup: 'campaigns.build.amd_action_opener_then_hangup_hint',
};

interface AnsweringMachineActionProps {
  senderPath: string;
}

/**
 * Per-sender voicemail action for AMD-capable voice senders. Unset means the
 * platform default (keep the call going), shown as the "continue" option.
 */
export const AnsweringMachineAction = ({ senderPath }: AnsweringMachineActionProps) => {
  const { t } = useTranslation();
  const { control } = useFormContext<CampaignForm>();

  return (
    <FormField
      control={control}
      name={senderFieldPath(senderPath, 'amdOnMachine')}
      render={({ field }) => {
        const value = (field.value as AmdOnMachine | null | undefined) ?? CONTINUE;
        return (
          <FormItem className="space-y-1">
            <FormLabel className="text-sm leading-none">
              {t('campaigns.build.amd_action_title')}
            </FormLabel>
            <FormDescription>{t('campaigns.build.amd_action_description')}</FormDescription>
            <Select
              value={value}
              onValueChange={(next: AmdActionOption) => {
                // null, not undefined: removing the key outright leaves the
                // control showing its old state
                field.onChange(next === CONTINUE ? null : next);
              }}
            >
              {/* On the trigger, not the Select root: the root renders no DOM node and drops id/aria */}
              <FormControl>
                <SelectTrigger className="w-full">
                  {/* Trigger renders just the short label, not the rich item below. */}
                  <SelectValue>{t(AMD_ACTION_LABEL_KEYS[value])}</SelectValue>
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {AMD_ACTION_OPTIONS.map((option) => (
                  <SelectItem
                    key={option}
                    value={option}
                    textValue={t(AMD_ACTION_LABEL_KEYS[option])}
                  >
                    <div className="flex flex-col gap-0.5 py-0.5">
                      <span className="font-medium">{t(AMD_ACTION_LABEL_KEYS[option])}</span>
                      <span className="text-xs text-foreground-muted">
                        {t(AMD_ACTION_HINT_KEYS[option])}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormItem>
        );
      }}
    />
  );
};
